import React, { useMemo, useState } from "react";

import { Box, Button, CircularProgress, Divider, Grid, IconButton, Menu, MenuItem, Slider, Typography } from "@mui/material";
import { Add, Edit, MoreVert } from "@mui/icons-material";

import { DataLayout, FieldControl, StatusChip, ExpandableText } from "../../../shared/components";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { formatDate } from "../../../shared/utils/formatDate";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useFetchRisk } from "../mutation";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

interface RaiseMitigationProps {
    isMitigationFetching: boolean;
    isMitigationPending: boolean;
    setMitigationsData: (data: any) => void;
    onClearMitigation: () => void;
    isSaving: boolean;
    onSaveMitigation: () => void;
    editMitigationStatus: any;
    mitigationData: any;
    mitigationsData: any;
    onEditMitigation: (cause: any) => void;
    riskId: string | null;
}

export const RaiseMitigation: React.FC<RaiseMitigationProps> = ({ riskId, mitigationsData, onEditMitigation, isMitigationFetching, isMitigationPending, setMitigationsData, onClearMitigation, isSaving, onSaveMitigation, editMitigationStatus, mitigationData }) => {

    const { isAllowedAddMitigation, isAllowedEditMitigation } = useCapabilities();

    const [formStatus, setFormStatus] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { getFilterOptions } = useDisplayField([]);

    const handleFieldChange = (key: string, value: any) => {
        setMitigationsData({ ...mitigationsData, [key]: value });
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!mitigationsData.impactCategory) newErrors.impactCategory = "Impact is required";
        if (!mitigationsData.impactTitle) newErrors.impactTitle = "Title is required";
        if (!mitigationsData.impactDescription) newErrors.impactDescription = "Description is required";
        if (!mitigationsData.owner_id) newErrors.owner_id = "Owner is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const { riskData } = useFetchRisk({ riskId });

    const { data: stakeholders, isLoading: isLoadingUsers } = useGetJeopardyStakeholders({ params: { project_id: riskData?.project_id, programme_id: riskData?.programme_id, portfolio_id: riskData?.portfolio_id }})

    const userOptions = useMemo(() => {
      return (
        stakeholders?.data?.flatMap((role: any) =>
          role?.users?.map((user: any) => ({
            role: role.role_name,
            name: user.user_name,
            phone: user.phone || "N/A",
            email: user.email,
            id: user.user_id,
          })),
        ) || []
      );
    }, [stakeholders, isLoadingUsers]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<any>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cause: any) => {
        event.stopPropagation();
        setSelected(cause);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (isMitigationFetching || isMitigationPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} sx={{ pr: 1 }}>
            {
                !formStatus && isAllowedAddMitigation && (
                    <Button onClick={() => { setErrors({}); setFormStatus(true); }} variant="outlined">Add Mitigation</Button>
                )
            }
            {
                formStatus && (
                    <DataLayout title={"Add Mitigations"} bgVariant="filled">
                        <Grid container spacing={2} p={2}>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactCategory"}
                                    label={"Impact"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_IMPACT').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={mitigationsData.impactCategory}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactCategory}
                                    helperText={errors.impactCategory}
                                    required
                                />
                            </Grid>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactTitle"}
                                    label={"Title"}
                                    variant={"input"}
                                    value={mitigationsData.impactTitle}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactTitle}
                                    helperText={errors.impactTitle}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    keyName={"impactDescription"}
                                    label={"Description"}
                                    variant={"input"}
                                    multiline
                                    value={mitigationsData.impactDescription}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactDescription}
                                    helperText={errors.impactDescription}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                keyName="owner_id"
                                label="Owner"
                                variant="select"
                                options={userOptions?.map((value) => {
                                    return {
                                        label: value.name,
                                        value: value.id
                                    }
                                })}
                                value={mitigationsData.owner_id}
                                onChange={handleFieldChange}
                                error={!!errors.owner_id}
                                helperText={errors.owner_id}
                                disabled={isLoadingUsers}
                                required
                            />
                            </Grid>
                            <Grid size={12} container border={1} borderColor={'divider'} borderRadius={1} padding={2} spacing={2}>
                                <Grid size={12} display={'flex'} alignItems={'center'} gap={1}>
                                    <Typography variant="subtitle2" color="text.primary">Impact After Mitigation</Typography>
                                    <Box ml={'auto'} display={'flex'} alignItems={'center'} gap={1}>
                                        <Typography variant="caption" color="text.secondary">Score</Typography>
                                        <StatusChip label={String(Number(mitigationsData.impact) * Number(mitigationsData.probability))} color="amber" variant="filled" sx={{ borderRadius: '4px', px: .5 }} />
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Divider />
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Impact</Typography>
                                        <Slider
                                            value={mitigationsData.impact}
                                            onChange={(_, value) =>
                                                setMitigationsData({ ...mitigationsData, impact: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Probability</Typography>
                                        <Slider
                                            value={mitigationsData.probability}
                                            onChange={(_, value) =>
                                                setMitigationsData({ ...mitigationsData, probability: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button
                                    sx={{ color: 'text.primary' }}
                                    disabled={isSaving}
                                    onClick={() => {
                                        onClearMitigation();
                                        setErrors({});
                                        setFormStatus(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    startIcon={<Add />}
                                    loading={isSaving}
                                    disabled={isSaving}
                                    onClick={() => {
                                        if (validate()) {
                                            onSaveMitigation();
                                        }
                                    }}
                                >
                                    {editMitigationStatus.editMitigationStatus ? 'Edit mitigation' : 'add mitigations'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DataLayout>
                )
            }

            <Box display={'flex'} flexDirection={'column'} gap={2} mt={2}>
                {
                    mitigationData?.length == 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No Mitigations found.</Typography> :
                        mitigationData.map((mitigation: any) => (
                            <Box display={'flex'} gap={1} key={mitigation.id} border={1} borderColor={'divider'} flexWrap={'wrap'} justifyContent={'space-between'} alignItems={'flex-start'} borderRadius={1} padding={2}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'start'} gap={.5} flex={1}>
                                    <ExpandableText 
                                        text={mitigation.title || 'N/A'} 
                                        variant="subtitle2" 
                                        color="text.primary"
                                        maxLines={1} 
                                        maxWidth="100%" 
                                    />
                                    {/* <Typography variant="subtitle2" color="text.primary">{mitigation.title || 'N/A'}</Typography> */}
                                    <ExpandableText 
                                        text={mitigation.description || 'Description Not Provided'} 
                                        variant="caption" 
                                        color="text.secondary"
                                        maxLines={2} 
                                        maxWidth="100%" 
                                    />
                                    <Box display={'flex'} alignItems={'center'} gap={.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            {mitigation.owner_display_text || 'No Owner Assigned'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            •
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Last Review: {mitigation.updated_at ? formatDate(mitigation.updated_at) : formatDate(mitigation.created_at)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5}>
                                    {
                                        isAllowedEditMitigation && (
                                            <IconButton onClick={(e) => handleMenuOpen(e, mitigation)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5} width={'100%'}>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={mitigation.residual_impact || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={mitigation.residual_probability || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                </Box>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onEditMitigation(selected);
                                            setErrors({});
                                            setFormStatus(true);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography ml={1}>Edit</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ))
                }
            </Box>
        </Box>
    )
}